import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Phone, MapPin, MessageCircle } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';
import './Footer.css';

const InstagramIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
);
const FacebookIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);
const Footer = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const response = await axiosInstance.post('/newsletter/subscribe', { email, firstName });
      if (response.data.success) {
        toast.success('Successfully subscribed to our newsletter!');
        setEmail('');
        setFirstName('');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="footer">

      {/* Newsletter Strip */}
      <div className="footer-newsletter-strip">
        <div className="footer-container">
          <div className="newsletter-strip-inner">
            <div className="newsletter-strip-left">
              <div className="newsletter-icon-wrap">
                <Mail size={22} color="#29ABE2" />
              </div>
              <div>
                <h3 className="newsletter-strip-title">Stay in the loop</h3>
                <p className="newsletter-strip-sub">Exclusive deals, new arrivals & loyalty updates.</p>
              </div>
            </div>
            <form onSubmit={handleSubscribe} className="newsletter-strip-form">
              <input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="nl-input"
              />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="nl-input"
                required
              />
              <button type="submit" className="nl-btn" disabled={subscribing}>
                {subscribing ? 'Subscribing…' : <>Subscribe <ArrowRight size={15} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="footer-main">
        <div className="footer-container">
          <div className="footer-grid">

            {/* Brand Column */}
            <div className="footer-brand-col">
              <Link to="/" className="footer-logo-link">
                <img src={logo} alt="Aurora Seafood" className="footer-logo-img" />
              </Link>
              <p className="footer-tagline">
                Premium seafood & quality meats delivered fresh to your door.
                Taste the difference. Trust the quality.
              </p>
              <div className="footer-contact-list">
                <a href="tel:+233000000000" className="footer-contact-item">
                  <Phone size={14} />
                  +233 20 092 6872
                </a>
                <a href="mailto:hello@auroraseafood.com" className="footer-contact-item">
                  <Mail size={14} />
                  sales@auroragh.com
                </a>
                <span className="footer-contact-item">
                  <MapPin size={14} />
                  Accra, Ghana
                </span>
              </div>
              <div className="footer-socials">
                <a href="https://www.facebook.com/auroraseafood" className="social-icon-btn" aria-label="Facebook">
                  <FacebookIcon size={16} />
                </a>
                <a href="https://www.instagram.com/auroraghofficial/" className="social-icon-btn" aria-label="Instagram">
                  <InstagramIcon size={16} />
                </a>
                <a href="https://wa.me/233200926872" className="social-icon-btn social-icon-btn--whatsapp" aria-label="WhatsApp">
                  <MessageCircle size={16} />
                </a>
              </div>
            </div>

            {/* Shop Column */}
            <div className="footer-col">
              <h4 className="footer-heading">Shop</h4>
              <Link to="/shop" className="footer-link">All Products</Link>
              <Link to="/shop?category=fresh" className="footer-link">Fresh</Link>
              <Link to="/shop?category=frozen" className="footer-link">Frozen</Link>
              <Link to="/shop?category=meat" className="footer-link">Meat</Link>
              <Link to="/shop?category=chicken" className="footer-link">Chicken</Link>
              <Link to="/shop?class=FIRST" className="footer-link">First Class</Link>
            </div>

            {/* Account Column */}
            <div className="footer-col">
              <h4 className="footer-heading">Account</h4>
              <Link to="/register" className="footer-link">Create Account</Link>
              <Link to="/login" className="footer-link">Login</Link>
              <a href="https://app.auroragh.com/dashboard" className="footer-link">My Dashboard</a>
              <a href="https://app.auroragh.com/dashboard/orders" className="footer-link">My Orders</a>
              <a href="https://app.auroragh.com/dashboard/loyalty" className="footer-link">Loyalty Program</a>
              <a href="https://app.auroragh.com/dashboard/wallet" className="footer-link">My Wallet</a>
            </div>

            {/* Support Column */}
            <div className="footer-col">
              <h4 className="footer-heading">Support</h4>
              <a href="/contact" className="footer-link">Contact Us</a>
              <a href="/faq" className="footer-link">FAQs</a>
              <a href="/shipping-policy" className="footer-link">Shipping Policy</a>
              <a href="/return-policy" className="footer-link">Return Policy</a>
              <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
              <a href="/terms" className="footer-link">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom-bar">
        <div className="footer-container">
          <div className="footer-bottom-inner">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Aurora Seafood. All rights reserved.
            </p>
            <div className="footer-bottom-links">
              <a href="/privacy-policy">Privacy Policy</a>
              <span>·</span>
              <a href="/terms">Terms</a>
              <span>·</span>
              <a href="/sitemap">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;